import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import {
  sourceDefaultStainsService,
  type CreateSourceDefaultStainData,
  type UpdateSourceDefaultStainData,
  type SourceDefaultStainSearchParams,
} from "../../services/admin/sourcedefaultstains.service";

export function useSourceDefaultStains(
  params?: SourceDefaultStainSearchParams
) {
  return useQuery({
    queryKey: ["admin", "source_default_stains", params],
    queryFn: () => sourceDefaultStainsService.getAll(params),
  });
}

export function useSourceDefaultStain(id: string) {
  return useQuery({
    queryKey: ["admin", "source_default_stains", id],
    queryFn: () => sourceDefaultStainsService.getById(id),
    enabled: !!id,
  });
}

export function useSourceDefaultStainsBySourceId(sourceId: string) {
  return useQuery({
    queryKey: ["admin", "source_default_stains", "by_source", sourceId],
    queryFn: () => sourceDefaultStainsService.getBySourceId(sourceId),
    enabled: !!sourceId,
  });
}

export function useCreateSourceDefaultStain() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateSourceDefaultStainData) =>
      sourceDefaultStainsService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "source_default_stains"],
      });
      notifications.show({
        title: "Success",
        message: "Source default stain created successfully",
        color: "green",
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to create source default stain",
        color: "red",
      });
    },
  });
}

export function useUpdateSourceDefaultStain() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateSourceDefaultStainData) =>
      sourceDefaultStainsService.update(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "source_default_stains"],
      });
      notifications.show({
        title: "Success",
        message: "Source default stain updated successfully",
        color: "green",
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to update source default stain",
        color: "red",
      });
    },
  });
}

export function useDeleteSourceDefaultStain() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => sourceDefaultStainsService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "source_default_stains"],
      });
      notifications.show({
        title: "Success",
        message: "Source default stain deleted successfully",
        color: "green",
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to delete source default stain",
        color: "red",
      });
    },
  });
}
